import React, {useEffect, useState, useRef, useContext} from "react";
import { useSelector, useDispatch } from "react-redux";
import { GlobalStore } from "../../reduxStore";
import VehicleResult from "./VehicleResult";
import VehicleSearch from "./VehicleSearch";
import MobileStepper from "@material-ui/core/MobileStepper";
import { Button } from "@mui/material";
import SearchFilter from "./SearchFilter";
import { translateService } from "../../services/TranslateService";
import { Card } from "react-bootstrap";
import NoResults from "./NoResults";
import styles from "./VehicleSearch.module.scss";
import Tippy from "@tippyjs/react";
import Translate from 'react-translate-component';
import { IOnSale, IVehicleSearch } from "../../interfaces/VehicleSearch";
import moment from "moment";
import OnSale from "./OnSale/OnSale";
import { DataContext, ISearchParameters } from "../../contexts/dataContext";
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import DefaultVehicleResult from './DefaultVehicleResult';
import DefaultResults from "./DefaultResultsBeforeSearch.json";
import {isUnderTwentyOne} from "../MakeReservation/util";
import {Steps} from "../reservation-steps/steps";
import {OneWaySearchTab, SearchTab} from "./SearchTab/SearchTab";

interface IApiResult {
    count: number,
    vehicles: IVehicleSearch[],
    onsales: IOnSale[],
    extraOnsales?: IOnSale[]
}

export default function SearchResultsContainer({ setIsSearching, setRedirect }: {
  setIsSearching: Function,
  setRedirect: Function,
}) {
  const apiService = useSelector((state: GlobalStore) => state.chauffeurReducer.apiService);
  const dispatch = useDispatch();
  const [searchResults, setSearchResults] = useState<IVehicleSearch[]>([]);
  const [onSaleResults, setOnSaleResults] = useState<IOnSale[]>([]);
  const numPerPage = 10;
  const [page, _setPage] = React.useState(0);
  const [showBusy, setShowBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [totalVehicles, setTotalVehicles] = useState(0);
  const [showNothing, setShowNothing] = useState(false);
  const [detailsVisible, setDetailsVisible] = React.useState(-1);
  const dataContext = useContext(DataContext);
  const [showFilter, setShowFilter] = useState<boolean>(false); // controls filter view only, results separated to show filter when no results on selected filters
  const [showResults, setShowResults] = useState<boolean>(false);
  const latestFetch = useRef(0);
  const scrollRef = useRef<null | HTMLElement>(null);
  const timeoutId = useRef(0);
  const defaultResults = DefaultResults.vehicles;

  useEffect(() => {
    if(dataContext.showDefaultResults && dataContext.mapEnabled && !dataContext.oneWayRes){
      dataContext.changeMapData(defaultResults ?? []); // set map data to default results
    }
  }, [dataContext.showDefaultResults, dataContext.mapEnabled, dataContext.oneWayRes])


  useEffect(() => {
    setDetailsVisible(-1)
  }, [page]);

  useEffect(() => {
    if(dataContext.enableOneWay && dataContext.oneWayRoutes.length === 0 && dataContext.oneWayRes){
      // one way must be selected to retrieved routes (to save app startup loading, etc.)
      // only get routes if entire app has oneway feature enabled
      // and when route array is empty (prevents recall every time user returns to search page)
      setShowBusy(true);
      apiService?.vehicle.getOneWayCityRoutes(dataContext.loggedIn ?? false)
          .then(r => {
            console.log(r);
            dataContext.changeOneWayRoutes(r);
            dataContext.sendGaEvent('searchOneWayCars', 'oneWayRoutesSuccess', '', 0);
            setErrorMsg("")
            setShowBusy(false);
          })
          .catch(e => {
            setErrorMsg(translateService.translate('vehicleSearch.errorLoadingResults'));
            dataContext.sendGaEvent('searchOneWayCars', 'oneWayRoutesFail', '', 0);
            console.log(e);
            setShowBusy(false);
          }); }
  }, [apiService, dataContext.enableOneWay, dataContext.oneWayRoutes, dataContext.oneWayRes]);

  useEffect(() => {
    //debounce
    clearTimeout(timeoutId.current)
    timeoutId.current = window.setTimeout(() => {
      doSearch()
    }, 300);

    const UseSearchResults = (thisFetch: number, results: IApiResult) => {
      if (thisFetch === latestFetch.current) {
       let onSaleArr: IOnSale[] = [];
        if(results.onsales && results.onsales.length < 10 && results.extraOnsales && results.extraOnsales.length > 0){
          onSaleArr = [...results.onsales];
          let extraOnSaleArr: IOnSale[] = [...results.extraOnsales];
          onSaleArr = onSaleArr.concat(extraOnSaleArr);
        } else {
          onSaleArr = [...results.onsales];
        }
        setSearchResults(results.vehicles ?? []);
        setOnSaleResults(onSaleArr ?? []);
        setTotalVehicles(results.count);
        setErrorMsg("");
        scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        if (dataContext.user)
          sessionStorage.setItem("user", JSON.stringify(dataContext.user));
        sessionStorage.setItem("time", moment().format())
      }
    }

    const doSearch = async () => {
      setSearchResults([]);
      if(!dataContext.oneWayRes){
        if(!dataContext.searchParameters?.location || !apiService)
          return
      } else {
        if(!dataContext.searchParameters?.pickupCity || !dataContext.searchParameters?.returnCity || !apiService)
          return
      }

      dataContext.changeShowDefaultResults(false);
      dataContext.changeMapData([]);
      setShowBusy(true);
      const thisFetch = ++latestFetch.current;

      if(!dataContext.oneWayRes) {
        try {
          const params = { ...dataContext.searchParameters, showStart: 0, showAmount: 1000 };
          // List
          // we will take 1000 results as the api3 is so slow and then take 10 of them to list view but use the 1000 results in the map
          const mapData = await apiService?.vehicle.searchWithParams(params);
          const start = dataContext.searchParameters.showStart === undefined ? 0 : dataContext.searchParameters.showStart;
          const end = start + (dataContext.searchParameters.showAmount === undefined ? 10 : dataContext.searchParameters.showAmount);
          let results = {
                        count:mapData.count,
                        vehicles:mapData.vehicles.slice(start,end),
                        onsales:mapData.onsales,
                        extraOnsales: mapData.extraOnsales
          };
          dataContext.sendGaEvent('searchCars', 'searchSuccess', '', 0);
          UseSearchResults(thisFetch, results);
          // Map
          if(dataContext.mapEnabled){
            // We do not have to get another set of results as we already have all of them
            dataContext.changeMapData(mapData.vehicles ?? []);
          }
          setShowBusy(false);
        } catch (e) {
          setErrorMsg(translateService.translate('vehicleSearch.errorLoadingResults'));
          console.log(e);
          dataContext.sendGaEvent('searchCars', 'searchResultsFail', '', 0);
          setShowBusy(false);
          scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        try {
          const params = { ...dataContext.searchParameters, showStart: 0, showAmount: 1000 };
          // List
          const mapData = await apiService?.vehicle.oneWaySearchWithParams(params, dataContext.loggedIn ?? false);
          // const start = dataContext.searchParameters.showStart === undefined ? 0 : dataContext.searchParameters.showStart;
          // const end = start + (dataContext.searchParameters.showAmount === undefined ? 10 : dataContext.searchParameters.showAmount);
          let results: IApiResult = {
            count:mapData.count,
            vehicles:mapData.vehicles, //.slice(start,end),
            // do not restrict one-way count, this will be used for sorting and will get updated after sorting and...
            // changing page or searchResults values will select the required data for displaying per page view (under 10)
            onsales:mapData.onsales,
            extraOnsales:mapData.extraOnsales
          };
          dataContext.sendGaEvent('searchOneWayCars', 'oneWaySearchSuccess', '', 0);
          UseSearchResults(thisFetch, results);
          // Map
          if(dataContext.mapEnabled){
            dataContext.changeMapData(mapData.vehicles ?? []);
          }
          setShowBusy(false);
        } catch (e) {
          setErrorMsg(translateService.translate('vehicleSearch.errorLoadingResults'));
          console.log(e);
          dataContext.sendGaEvent('searchOneWayCars', 'oneWaySearchResultsFail', '', 0);
          setShowBusy(false);
          scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
      setShowNothing(false);
      setIsSearching(true)
    }
    return () => clearTimeout(timeoutId.current);
  }, [dataContext.searchParameters]);

  useEffect(() => {
    let reservationCreated: any = sessionStorage.getItem("reservationCreated");
    if (reservationCreated) {
      reservationCreated = !!JSON.parse(reservationCreated!);
      if (reservationCreated === true) {
        dataContext.clearSelections();
        dispatch({ type: "CLEAR_SELECTIONS", payload: null });
      }
    }
    //clear saved parameters after 30 mins
    let t = sessionStorage.getItem("time");
    if (t) {
      if (moment().diff(moment(t), "minutes") > 30) {
        dataContext.clearSelections();
        dispatch({ type: "CLEAR_SELECTIONS", payload: null });
        return;
      }
    }

    let c = sessionStorage.getItem("coupon");
    if (c) {
      dispatch({ type: "SET_DISCOUNT_COUPON", payload: JSON.parse(c!) });
    }

    let s = sessionStorage.getItem("searchParameters");
    if (s && s!=='undefined') { // note: not sure why string 'undefined' is even set here, but does show up on some reloads
      let paramsObject: ISearchParameters = JSON.parse(s!);
      dataContext.changeSearchParameters(paramsObject);
    }
  }, [])

  function setSearchParams(v: any) {
    let params: ISearchParameters;
    if (typeof v === 'function')
      params = v(dataContext.searchParameters);
    else
      params = v;
    dataContext.changeSearchParameters(params);
    if (params) {
      dataContext.changeSearchParameters(params);
    }
  }

  function selectVehicle(vehicle: IVehicleSearch) {
    if (vehicle.id !== dataContext.vehicle?.id) {
      sessionStorage.removeItem("customer");
      sessionStorage.removeItem("services");
    }

    if(dataContext.customer && dataContext.vehicle){
      if( dataContext.customer.dateOfBirth !== '' ||  dataContext.customer.personalIdCode !== ''){
        if(isUnderTwentyOne(dataContext.customer.dateOfBirth,  dataContext.customer.personalIdCode) &&  vehicle){
          if(vehicle.vehicleData.noundertwentyone){
            dataContext.changeTwentyModal(true);
            return;
          }
        }
      }
    }

    dataContext.changeVehicle(vehicle);
    dataContext.changeMapData([vehicle])
    dataContext.changeSearchParameters(dataContext.searchParameters);

    if (dataContext.user)
      sessionStorage.setItem("user", JSON.stringify(dataContext.user));
      sessionStorage.setItem("page", "1")
      dataContext.changeRoute("/makereservation")
      dataContext.changePage(1)
  }

  const clearResults = () => {
    setSearchResults([]);
    dataContext.changeMapData([]);
    setTotalVehicles(0);
    setShowNothing(true);
    setShowFilter(false);
    setShowResults(false);
  };

  const [currentPageResults, setCurrentPageResults] = useState<IVehicleSearch[]>( []);

  const setPage = (p: number) => {
    _setPage(prevPage => {
      const newPage = p;
      if (!dataContext.oneWayRes) {
        if(dataContext.filteredVehicles){
          const startIndex = newPage * numPerPage;
          const endIndex = Math.min(startIndex + numPerPage, dataContext.filteredVehicles?.length);
          setCurrentPageResults(dataContext.filteredVehicles.slice(startIndex, endIndex));
        } else {
          setCurrentPageResults([]);
        }
      } else {
        if(searchResults) {
          const startIndex = page * numPerPage;
          const endIndex = Math.min(startIndex + numPerPage, totalVehicles);
          setCurrentPageResults(searchResults.slice(startIndex, endIndex));
        } else {
          setCurrentPageResults([]);
        }
      }
      return newPage;
    });
  };

  useEffect(() => {
    if (!dataContext.oneWayRes) {
      if(dataContext.filteredVehicles){
        const startIndex = page * numPerPage;
        const endIndex = Math.min(startIndex + numPerPage, dataContext.filteredVehicles?.length);
        setCurrentPageResults(dataContext.filteredVehicles.slice(startIndex, endIndex));
      } else {
        setCurrentPageResults([]);
      }
    } else {
      if(searchResults.length) {
        const startIndex = page * numPerPage;
        const endIndex = Math.min(startIndex + numPerPage, totalVehicles);
        setCurrentPageResults(searchResults.slice(startIndex, endIndex));
      } else {
        setCurrentPageResults([]);
      }
    }
  }, [dataContext.filteredVehicles, page, numPerPage, searchResults]);

  const retrieveStarsData = async () => {
    sessionStorage.setItem('starsUpdatedDS', '1'); // 1 indicates that retrieval in progress
    try{
      const response = await fetch(dataContext.rootUrl+'/testimonials.json');
      const data = await response.json();
      // 'starsUpdatedDS': stars data update date time value (only fetch if last update was > 24 hours ago)
      // using sessionStorage as it updates faster than react states (used to prevent fetch before last call gets return value)
      sessionStorage.setItem('starsUpdatedDS', moment().format('DD.MM.YYYY HH:mm').toString())
      dataContext.changeStars(data);
      dataContext.sendGaEvent('searchCars', 'testimonialsLoadSuccess', '', 0);
    } catch (e) {
      console.log(e);
      // remove item indicator so fetch can be tried again on next search:
      sessionStorage.removeItem('starsUpdatedDS');
      dataContext.sendGaEvent('searchCars', 'testimonialsLoadFailed', '', 0);
    }
  }

  const getStarValue = () => {
    const starsLastRetrievedData = sessionStorage.getItem('starsUpdatedDS') ?? undefined;
    if (starsLastRetrievedData === '1') return; // retrieval in progress
    if(!starsLastRetrievedData || starsLastRetrievedData === '0') {
      retrieveStarsData();
    } else if (starsLastRetrievedData && starsLastRetrievedData !== '0') {
      if (moment(starsLastRetrievedData, 'DD.MM.YYYY HH:mm').isValid() &&
          moment(starsLastRetrievedData, 'DD.MM.YYYY HH:mm') < moment().subtract(24, 'hours'))
      {
        retrieveStarsData();
      }
    }
  }

  const retrieveAcrissData = async () => {
    sessionStorage.setItem('acrissCodesUpdatedDS', '1'); // 1 indicates that retrieval in progress
    try{
      const response = await fetch(dataContext.v3ApiUrl + '/anonymous/vehicles/get-acriss');
      const data = await response.json();
      // 'acrissCodesUpdatedDS': stars data update date time value (only fetch if last update was > 24 hours ago)
      // using sessionStorage as it updates faster than react states (used to prevent fetch before last call gets return value)
      sessionStorage.setItem('acrissCodesUpdatedDS', moment().format('DD.MM.YYYY HH:mm').toString())
      dataContext.changeAcrissCodeSet(data.data);
    } catch (e) {
      console.log(e);
      // remove item indicator so fetch can be tried again on next search:
      sessionStorage.removeItem('acrissCodesUpdatedDS');
    }
  }

  const getAcrissValues = () => {
    const acrissLastRetrievedData = sessionStorage.getItem('acrissCodesUpdatedDS') ?? undefined;
    if (acrissLastRetrievedData === '1') return; // retrieval in progress
    if(!acrissLastRetrievedData || acrissLastRetrievedData === '0') {
      retrieveAcrissData();
    } else if (acrissLastRetrievedData && acrissLastRetrievedData !== '0') {
      if (moment(acrissLastRetrievedData, 'DD.MM.YYYY HH:mm').isValid() &&
          moment(acrissLastRetrievedData, 'DD.MM.YYYY HH:mm') < moment().subtract(24, 'hours'))
      {
        retrieveAcrissData();
      }
    }
  }

  useEffect(() => {
    if(searchResults.length !==0 && !showBusy ){
      setShowFilter(true);
      setShowResults(true);
    } else if (searchResults.length===0 && dataContext.searchFiltersSelected && !showBusy && !showNothing) {
      // no results with filtered selections
      setShowFilter(true);
      setShowResults(false);
    } else {
      setShowFilter(false);
      setShowResults(false);
    }
    getStarValue();
    getAcrissValues();
  }, [showBusy, searchResults]);

  useEffect(() => {
    if(page !== 0) { // only do this once
      setPage(0);
    }
  }, [dataContext.filteredParams, searchResults]);

  const PageStepper = () => {
    const totalPages = !dataContext.oneWayRes ? Math.ceil((dataContext.filteredVehicles?.length || 0) / numPerPage) :
        Math.ceil((totalVehicles || 0) / numPerPage);

    return (
        <MobileStepper
            steps={totalPages}
            variant="text"
            style={{
              flexGrow: 1,
              borderRadius: '8px',
              boxShadow: '0 3px 6px rgb(0 0 0 / 12%)',
              marginBottom: '1.5rem'
            }}
            activeStep={page}
            position="static"
            backButton={
              <span>
          <Tippy className="tooltip-link"
                 zIndex={2}
                 content={<Translate content={'vehicleSearch.firstPage'} />}
          >
            <Button size="small"
                    className={styles.prevNextButton}
                    variant="text"
                    onClick={() => {
                      setPage(0);
                      scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      dataContext.sendGaEvent('changePage', 'startPage', '', 0);
                    }}
                    disabled={page <= 0}>
              &lt;&lt;
            </Button>
          </Tippy>
          <Button size="small"
                  className={styles.prevNextButton}
                  onClick={() => {
                    setPage(page - 1);
                    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    dataContext.sendGaEvent('changePage', 'prevPage', '', 0);
                  }}
                  disabled={page <= 0}>
            &lt;
          </Button>
        </span>
            }
            nextButton={
              <span>
          <Button size="small"
                  className={styles.prevNextButton}
                  onClick={() => {
                    setPage(page + 1);
                    scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    dataContext.sendGaEvent('changePage', 'nextPage', '', 0);
                  }}
                  disabled={page + 1 >= totalPages}>
            &gt;
          </Button>
          <Tippy className="tooltip-link"
                 zIndex={2}
                 content={<Translate content={'vehicleSearch.lastPage'} />}
          >
            <Button size="small"
                    className={styles.prevNextButton}
                    onClick={() => {
                      setPage(totalPages - 1);
                      scrollRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      dataContext.sendGaEvent('changePage', 'endPage', '', 0);
                    }}
                    disabled={page + 1 >= totalPages}>
              &gt;&gt;
            </Button>
          </Tippy>
        </span>
            }
        />
    )
  }

  return <div className={styles.searchContainer}>
    <VehicleSearch scrollRef={scrollRef} setErrorMsg={setErrorMsg} setSearchParams={setSearchParams} clearResults={clearResults} />

    {!!errorMsg &&
        <Card className={`card-body mb-4`}>
          <div className="text-center pt-3">{errorMsg}</div>
        </Card>}
    {showFilter && !dataContext.oneWayRes ? <SearchFilter /> : null}
    <span ref={scrollRef} id='scrollAnchor'></span>
    <Backdrop
        sx={{ color: '#fbb040', zIndex: (theme) => theme.zIndex.drawer + 10005 }}
        open={showBusy} >
      <CircularProgress color="inherit" />
      <div style={{marginLeft: '5px'}}><Translate content={'vehicleSearch1.loading'} /></div>
    </Backdrop>
    { !dataContext.oneWayRes && dataContext.showDefaultResults ? <div>
      <div style={{fontWeight: 600, textAlign:"center", justifyContent: "center", padding: 10, fontSize: 18, fontStyle: "italic" }}>
        {translateService.translate('vehicleSearch1.belowSamplesFillLocation')}
      </div>
          { defaultResults && defaultResults.map((vehicle, i) => {
            return <DefaultVehicleResult key={i} vehicle={vehicle}
                                         setRedirect={setRedirect}
                                         selectVehicle={()=> {
                                           document.getElementById("scrollAnchor")?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                           (document.querySelector('.searchbox-input') as HTMLElement)?.focus();
                                         }}
                                         detailsVisible={i === detailsVisible}
                                         setDetailsVisible={() => {setDetailsVisible(() => { return i === detailsVisible ? -1 : i });
                                           dataContext.sendGaEvent('reservation', 'showInfo', '', 0);}}
            /> })
          }
          <PageStepper/>
    </div> :
        <>
          {!dataContext.oneWayRes ?
              showResults && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 && <Steps /> :
              showResults && searchResults.length > 0 && <Steps/> }
        {showResults && !dataContext.oneWayRes && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 ? <OnSale sales={onSaleResults} selectVehicle={selectVehicle} /> : null}
        {!dataContext.oneWayRes && showResults && dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 ?  <SearchTab/> : null}
          {dataContext.oneWayRes && showResults && searchResults ?
              <OneWaySearchTab oneWayVehicleList={dataContext.mapData} setSearchResults={setSearchResults}/> : null }
        {!dataContext.oneWayRes ?
            dataContext.filteredVehicles && dataContext.filteredVehicles.length > 0 && showResults ? (
                <>
                  {!showBusy && currentPageResults.map((vehicle, i) => {
                    return(
                      <VehicleResult key={i} vehicle={vehicle}
                                     stars={dataContext.stars ? dataContext.stars[vehicle.id] : undefined}
                                     setRedirect={setRedirect} selectVehicle={selectVehicle}
                                     detailsVisible={i === detailsVisible}
                                     setDetailsVisible={() => {setDetailsVisible(() => { return i === detailsVisible ? -1 : i });
                                       dataContext.sendGaEvent('reservation', 'showInfo', '', 0);}}
                                     setBusy={setShowBusy}/>
                    )
                  })
                  }
                  <PageStepper/>
                </>
            ) : <> {(!showBusy && !showNothing && dataContext.filteredVehicles?.length === 0) ? <NoResults /> : null} </>
            :
            <div> {searchResults && showResults ? (
                    <>
                      {!showBusy && currentPageResults.map((vehicle, i) => {
                        return <VehicleResult key={i} vehicle={vehicle}
                                              stars={dataContext.stars ? dataContext.stars[vehicle.id] : undefined}
                                              setRedirect={setRedirect} selectVehicle={selectVehicle}
                                              detailsVisible={i === detailsVisible}
                                              setDetailsVisible={() => {setDetailsVisible(() => { return i === detailsVisible ? -1 : i });
                                                dataContext.sendGaEvent('reservation', 'showInfo', '', 0);}}
                                              setBusy={setShowBusy}/>
                      })
                      }
                      <PageStepper/>
                    </>
                )
                : <> {(!showBusy && !showNothing) && <NoResults /> } </> }
            </div>
        }
      </>
    }
  </div>;
};
